import React from 'react';
import type { MenuItem } from '../../types';
import StarSolidIcon from '../icons/StarSolidIcon';
import MenuItemImage from '../menu/MenuItemImage';

interface MenuItemCardProps {
  item: MenuItem;
  onAddItem: (item: MenuItem, selectedMeatOption?: string) => void;
  availableQuantity?: number; // Available quantity (dailyAvailability - reserved)
}

const MenuItemCard: React.FC<MenuItemCardProps> = ({ item, onAddItem, availableQuantity }) => {
  const hasMeatOptions = item.meatOptions && item.meatOptions.length > 0;
  // Use availableQuantity if provided (accounts for reserved items), otherwise fall back to dailyAvailability
  const available = availableQuantity !== undefined ? availableQuantity : (item.dailyAvailability ?? 0);
  const isFinished = item.hasDailyAvailability && available <= 0;
  const isAvailable = item.hasDailyAvailability && available > 0;
  
  return (
    <button
      onClick={() => !isFinished && onAddItem(item)}
      disabled={isFinished}
      data-testid={`menu-item-${item.id}`}
      data-menu-item-name={item.name}
      data-has-meat-options={hasMeatOptions ? 'true' : 'false'}
      className={`group bg-white rounded-md shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-200 overflow-hidden text-center focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-opacity-50 relative flex flex-col ${
        isFinished ? 'opacity-60 cursor-not-allowed' : ''
      }`}
    >
       {item.isSignatureDish && (
        <div className="absolute top-0.5 right-0.5 bg-gradient-to-br from-yellow-400 to-yellow-500 text-white p-0.5 rounded-full z-10 shadow-sm" title="Signature Dish">
          <StarSolidIcon className="w-2.5 h-2.5" />
        </div>
      )}
      {isFinished && (
        <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-[8px] sm:text-[9px] font-bold py-0.5 z-10">
          Finished
        </div>
      )}
      {isAvailable && (
        <div className="absolute top-0 left-0 right-0 bg-green-500 text-white text-[8px] sm:text-[9px] font-bold py-0.5 z-10">
          {available} available
        </div>
      )}
      <MenuItemImage src={item.imageUrlThumb || item.imageUrl || `https://picsum.photos/seed/${item.id}/200`} alt={item.name} className={`w-full h-10 sm:h-12 md:h-14 group-hover:brightness-110 transition-all ${isFinished ? 'grayscale' : ''}`} width={120} height={56} />
      <div className="p-1 sm:p-1.5 flex flex-col gap-0.5">
        <h3 className="font-bold text-gray-800 text-[9px] sm:text-[10px] md:text-xs leading-tight break-words">{item.name}</h3>
        {item.description && (
          <p className="text-[7px] sm:text-[8px] text-gray-500 line-clamp-2 mt-0.5">
            {item.description}
          </p>
        )}
        {hasMeatOptions && (
          <p className="text-[8px] sm:text-[9px] text-gray-500">Multiple options</p>
        )}
        <p className="text-[8px] sm:text-[9px] text-pink-600 font-semibold">
          {item.soldByWeight ? 'By weight' : `$${item.price.toFixed(2)}`}
        </p>
      </div>
    </button>
  );
};

export default MenuItemCard;